import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Copy, Plus, Trash2, Key, Shield, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const PROJECT_ID = import.meta.env.VITE_SUPABASE_PROJECT_ID;
const BASE_URL = `https://${PROJECT_ID}.supabase.co/functions/v1/api-keys-admin`;

interface ApiKey {
  id: string;
  api_key: string;
  name: string;
  daily_limit: number;
  requests_today: number;
  last_request_date: string;
  is_active: boolean;
  created_at: string;
}

export default function AdminApiKeys() {
  const navigate = useNavigate();
  const [adminSecret, setAdminSecret] = useState("");
  const [authenticated, setAuthenticated] = useState(false);
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyLimit, setNewKeyLimit] = useState("10");

  const headers = useCallback(
    () => ({
      "x-admin-secret": adminSecret,
      "Content-Type": "application/json",
    }),
    [adminSecret],
  );

  const fetchKeys = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}?action=list`, { headers: headers() });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setKeys(data.keys ?? []);
    } catch (e: unknown) {
      toast.error("Avainten haku epäonnistui", { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  }, [headers]);

  const handleLogin = async () => {
    if (!adminSecret.trim()) return;
    setLoading(true);
    try {
      const res = await fetch(`${BASE_URL}?action=list`, {
        headers: { "x-admin-secret": adminSecret, "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setAuthenticated(true);
      setKeys(data.keys ?? []);
    } catch {
      toast.error("Väärä admin-salasana");
    } finally {
      setLoading(false);
    }
  };

  const createKey = async () => {
    if (!newKeyName.trim()) {
      toast.error("Anna avaimelle nimi");
      return;
    }
    try {
      const res = await fetch(`${BASE_URL}?action=create`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ name: newKeyName, daily_limit: parseInt(newKeyLimit) || 10 }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      toast.success("Avain luotu!", { description: data.key.api_key });
      setNewKeyName("");
      setNewKeyLimit("10");
      fetchKeys();
    } catch (e: unknown) {
      toast.error("Luonti epäonnistui", { description: (e as Error).message });
    }
  };

  const toggleKey = async (id: string, is_active: boolean) => {
    try {
      const res = await fetch(`${BASE_URL}?action=toggle`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ id, is_active }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setKeys((prev) => prev.map((k) => (k.id === id ? { ...k, is_active } : k)));
      toast.success(is_active ? "Avain aktivoitu" : "Avain deaktivoitu");
    } catch (e: unknown) {
      toast.error("Päivitys epäonnistui", { description: (e as Error).message });
    }
  };

  const deleteKey = async (id: string) => {
    if (!confirm("Haluatko varmasti poistaa tämän avaimen?")) return;
    try {
      const res = await fetch(`${BASE_URL}?action=delete`, {
        method: "POST",
        headers: headers(),
        body: JSON.stringify({ id }),
      });
      const data = await res.json();
      if (!data.ok) throw new Error(data.error);
      setKeys((prev) => prev.filter((k) => k.id !== id));
      toast.success("Avain poistettu");
    } catch (e: unknown) {
      toast.error("Poisto epäonnistui", { description: (e as Error).message });
    }
  };

  const copyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    toast.success("Kopioitu leikepöydälle");
  };

  // ── Login gate ──
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-border/50">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <CardTitle className="text-foreground">API Admin</CardTitle>
            <p className="text-sm text-muted-foreground">Syötä admin-salasana jatkaaksesi</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <Input
              type="password"
              placeholder="Admin secret"
              value={adminSecret}
              onChange={(e) => setAdminSecret(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleLogin()}
              className="bg-secondary/50 border-border"
            />
            <Button onClick={handleLogin} disabled={loading} className="w-full">
              {loading ? "Tarkistetaan..." : "Kirjaudu"}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ── Admin panel ──
  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <Key className="w-5 h-5 text-primary" />
                API-avainten hallinta
              </h1>
              <p className="text-sm text-muted-foreground">{keys.length} avainta</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={fetchKeys} disabled={loading}>
            Päivitä
          </Button>
        </div>

        {/* Create new key */}
        <Card className="border-border/50">
          <CardHeader>
            <CardTitle className="text-base text-foreground">Luo uusi avain</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-3">
              <Input
                placeholder="Avaimen nimi (esim. 'Oma sovellus')"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                className="flex-1 bg-secondary/50 border-border"
              />
              <Input
                type="number"
                placeholder="Päiväraja"
                value={newKeyLimit}
                onChange={(e) => setNewKeyLimit(e.target.value)}
                className="w-28 bg-secondary/50 border-border"
                min={1}
                max={1000}
              />
              <Button onClick={createKey} className="gap-2">
                <Plus className="w-4 h-4" /> Luo
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Key list */}
        <div className="space-y-3">
          {keys.map((k) => (
            <Card key={k.id} className={`border-border/50 transition-opacity ${!k.is_active ? "opacity-50" : ""}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="space-y-1.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-foreground">{k.name}</span>
                      <Badge variant={k.is_active ? "default" : "secondary"} className="text-xs">
                        {k.is_active ? "Aktiivinen" : "Pois käytöstä"}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <code className="text-xs text-muted-foreground font-mono bg-secondary/50 px-2 py-0.5 rounded truncate max-w-[280px]">
                        {k.api_key}
                      </code>
                      <Button variant="ghost" size="icon" className="h-6 w-6 shrink-0" onClick={() => copyKey(k.api_key)}>
                        <Copy className="w-3 h-3" />
                      </Button>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span>
                        Käyttö tänään:{" "}
                        <span className="text-foreground font-medium">
                          {k.requests_today}/{k.daily_limit}
                        </span>
                      </span>
                      <span>
                        Luotu:{" "}
                        {new Date(k.created_at).toLocaleDateString("fi-FI")}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <Switch
                      checked={k.is_active}
                      onCheckedChange={(checked) => toggleKey(k.id, checked)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => deleteKey(k.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}

          {keys.length === 0 && !loading && (
            <div className="text-center py-12 text-muted-foreground">
              <Key className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p>Ei API-avaimia vielä</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
